import { Side, ZoneStrategy } from '@ekkogmbh/apisdk';
import { Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

export interface StrategyConfig {
  label: string;
  key: string;
  default?: string | string[];
  options?: string[];
  hint?: string;
  tooltip?: ReactNode;
  variant?: 'text' | 'number' | 'toggle';
  /** makes a form for a list */
  list?: boolean;
}

type MergeStrategy = 'distribute' | 'multiplex' | 'multi-order' | 'append';

/** maps a strategy name to pairs of config-key => label */
export const MergeStrategiesConfigMap: Record<string, StrategyConfig[]> = {
  append: [],
  /* excluded because not yet fully functional
  distribute: [
    {
      label: 'Distribute by',
      key: 'distributeBy',
      default: Side.SOURCE,
      options: [Side.SOURCE, Side.DESTINATION],
    },
  ],
  multiplex: [
    {
      label: 'Multiplex by',
      key: 'multiplexBy',
      default: Side.SOURCE,
      options: [Side.SOURCE, Side.DESTINATION],
    },
  ],
  */
  'multi-order': [
    {
      label: 'Side',
      key: 'side',
      default: Side.DESTINATION,
      options: [Side.SOURCE, Side.DESTINATION],
      hint: 'the pattern is applied to the selected side.',
    },
    {
      label: 'Pattern',
      key: 'pattern',
      default: '{{bp}}',
      hint: 'the {{bp}} keyword is required.',
      tooltip: (
        <Stack direction={'column'} spacing={2}>
          <Typography variant={'body1'} fontWeight={500}>
            Value pattern for the selected distribution side.
          </Typography>
          <Typography variant={'overline'}>Available keywords:</Typography>
          <Typography>{'{{bp}} - represents the blueprint position within the multi-order and is required'}</Typography>
          <Typography>{'{{v}} - represents the actual picking value within the blueprint (optional)'}</Typography>
          <Typography>
            {
              '{{a:annotation_key}} - represents an annotation value. Throws an error when the annotation is not set. (optional)'
            }
          </Typography>
        </Stack>
      ),
    },
    {
      label: 'Merge Metadata',
      key: 'mergeMetadata',
      list: true,
      tooltip: 'specified keys will be merged and ignored for equality checks between blueprint values',
    },
  ],
};

export const isMergeConfigValid = (strategy: ZoneStrategy): boolean => {
  const { name, configuration } = strategy;

  if (Object.keys(MergeStrategiesConfigMap).includes(name)) {
    return MergeStrategiesConfigMap[name as MergeStrategy].every((configRule) => {
      const { key } = configRule;
      const setValue = configuration[key];

      if (configRule.default !== undefined && setValue === undefined) {
        return false;
      }

      if (configRule.options && !configRule.options.includes(setValue as string)) {
        return false;
      }

      return true;
    });
  }

  return true;
};

type ConfirmStrategy = 'default' | 'strict' | 'random';
export const ConfirmStrategyConfigMap: Record<ConfirmStrategy, StrategyConfig[]> = {
  default: [
    {
      key: 'activeValueLimit',
      label: 'Active Value Limit',
      default: '3',
      variant: 'number',
    },
    {
      key: 'optionalActivatesBothSides',
      label: 'Also Activate Optional Side',
      variant: 'toggle',
      default: 'false',
      tooltip: 'Activates both sides when one side is configured to have optional confirmation',
    },
  ],
  strict: [],
  random: [
    {
      key: 'confirmationCountCompletesStep',
      label: 'Confirmation Count Completes Step',
      default: '0',
      hint: 'setting to 0 disables the limit',
      variant: 'number',
    },
    {
      key: 'activeValueLimit',
      label: 'Active Value Limit',
      default: '0',
      variant: 'number',
      hint: 'setting to 0 disables the limit',
    },
    {
      key: 'activeValueLimitAlternate',
      label: 'Active Value Limit Alternate',
      default: 'false',
      variant: 'toggle',
      tooltip: 'when an active value limit is set, alternates sides when activating new picking values',
    },
  ],
};
